import React from 'react';
import { Award, AlertTriangle, BarChart2 } from 'lucide-react';

const LevelRow = ({ item, rank, isTop }) => {
  const barColor = isTop ? 'bg-emerald-400' : 'bg-red-400';
  const barWidth = Math.min(item?.completionRate, 100);

  return (
    <div className="flex items-center gap-4 py-3 border-b border-white/5 last:border-0">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
        isTop ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'
      }`}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-semibold">{item?.label}</p>
        <div className="flex items-center gap-3 mt-1">
          <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${barColor} transition-all duration-500`}
              style={{ width: `${barWidth}%` }}
            />
          </div>
          <span className={`text-xs font-bold flex-shrink-0 ${isTop ? 'text-emerald-300' : 'text-red-300'}`}>
            {item?.completionRate}%
          </span>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-purple-200 text-xs">{item?.totalViews} views</p>
        <p className="text-purple-400 text-xs mt-0.5">{item?.uniqueViewers} learners</p>
      </div>
    </div>
  );
};

const TopBottomPerformers = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {[1, 2]?.map(i => (
          <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="h-5 bg-white/20 rounded animate-pulse w-40 mb-5" />
            {[1, 2, 3]?.map(j => (
              <div key={j} className="h-14 bg-white/10 rounded-xl animate-pulse mb-2" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  const top = data?.top || [];
  const bottom = data?.bottom || [];
  const hasData = top?.length > 0 || bottom?.length > 0;

  if (!hasData) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 text-center">
        <BarChart2 className="w-10 h-10 text-purple-400 mx-auto mb-3 opacity-50" />
        <p className="text-purple-300 text-lg font-medium">No level performance data yet</p>
        <p className="text-purple-400 text-sm mt-1">Rankings will appear once students start watching videos</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Top Performers */}
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-emerald-500/20 rounded-lg">
            <Award className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-white font-bold">Top Levels</h3>
            <p className="text-purple-300 text-xs">Highest avg completion rate</p>
          </div>
        </div>
        {top?.length === 0 ? (
          <p className="text-purple-400 text-sm text-center py-8">No data available</p>
        ) : (
          top?.map((item, i) => (
            <LevelRow key={item?.level || i} item={item} rank={i + 1} isTop={true} />
          ))
        )}
      </div>

      {/* Bottom Performers */}
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
        <div className="flex items-center gap-2 mb-5">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <h3 className="text-white font-bold">Needs Attention</h3>
            <p className="text-purple-300 text-xs">Lowest avg completion rate</p>
          </div>
        </div>
        {bottom?.length === 0 ? (
          <p className="text-purple-400 text-sm text-center py-8">No data available</p>
        ) : (
          bottom?.map((item, i) => (
            <LevelRow key={item?.level || i} item={item} rank={i + 1} isTop={false} />
          ))
        )}
      </div>
    </div>
  );
};

export default TopBottomPerformers;
